/**
 * Calendar refresh rules - pure, no store, no Dexie, no network.
 *
 * - A fetch that fails, or a feed whose events are all unreadable, NEVER replaces the data shown:
 *   the previous events stay and the status records the error.
 * - A legitimately empty feed (valid VCALENDAR, no events, nothing skipped) does replace it.
 * - An unchanged feed (same content hash) is not parsed again.
 * - The device-local cache is versioned and holds parsed events only, never occurrences
 *   (occurrences are expanded per window on load and memoised).
 */

import { ICalParseError, parseICalFeed, type ParseOptions, type ParsedFeed } from "./icalParser"
import type {
  CalendarErrorCode,
  CalendarStatus,
  ICalEventItem,
  SkippedEvent,
} from "./types"

export const CALENDAR_CACHE_VERSION = 2

/** Most skipped-event entries kept per calendar (they are shown, not stored for analysis). */
const MAX_SKIPPED_KEPT = 50

export interface CachedCalendar {
  id: string
  /** The URL the data was fetched from; data for another URL is never shown. */
  url: string
  /** Hash of the last parsed ICS text. */
  contentHash?: string
  /** X-WR-CALNAME of the feed. */
  calendarName?: string | null
  events: ICalEventItem[]
  status: CalendarStatus
}

export interface CalendarCacheFile {
  version: typeof CALENDAR_CACHE_VERSION
  calendars: CachedCalendar[]
}

export function emptyStatus(): CalendarStatus {
  return { eventCount: 0, recurringCount: 0, skipped: [], uncertainZones: [] }
}

/** The cached calendars, or `[]` for a cache of another version (re-fetched instead). */
export function readCalendarCache(value: unknown): CachedCalendar[] {
  let data = value
  if (typeof data === "string") {
    try {
      data = JSON.parse(data)
    } catch {
      return []
    }
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) return []
  const file = data as Partial<CalendarCacheFile>
  if (file.version !== CALENDAR_CACHE_VERSION || !Array.isArray(file.calendars)) return []
  return file.calendars.filter(
    (c): c is CachedCalendar =>
      !!c && typeof c.id === "string" && typeof c.url === "string" && Array.isArray(c.events),
  )
}

export function writeCalendarCache(calendars: CachedCalendar[]): string {
  const file: CalendarCacheFile = {
    version: CALENDAR_CACHE_VERSION,
    calendars: calendars.map((c) => ({
      id: c.id,
      url: c.url,
      contentHash: c.contentHash,
      calendarName: c.calendarName,
      events: c.events,
      status: c.status,
    })),
  }
  return JSON.stringify(file)
}

/** FNV-1a (32 bit) + length: cheap change detection for a feed's text. */
export function hashText(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return `${(h >>> 0).toString(16)}:${text.length}`
}

/** The status code of a failed proxy request as an error code (see the proxy contract). */
export function errorCodeForStatus(status: number): CalendarErrorCode {
  switch (status) {
    case 400:
      return "invalid-url"
    case 401:
    case 403:
      return "auth"
    case 413:
      return "too-large"
    case 429:
      return "rate-limited"
    case 502:
      return "upstream"
    case 504:
      return "timeout"
    default:
      return "http"
  }
}

export type FetchOutcome =
  | { kind: "parsed"; feed: ParsedFeed; hash: string }
  | { kind: "unchanged"; hash: string }
  | { kind: "error"; code: CalendarErrorCode; status?: number }

/** Parse a fetched feed text into an outcome (no parse when the text did not change). */
export function outcomeFromText(
  text: string,
  prev: CachedCalendar | undefined,
  opts?: ParseOptions,
): FetchOutcome {
  const hash = hashText(text)
  if (prev && prev.contentHash === hash && prev.status.lastOk !== undefined) {
    return { kind: "unchanged", hash }
  }
  try {
    return { kind: "parsed", feed: parseICalFeed(text, opts), hash }
  } catch (e) {
    return { kind: "error", code: e instanceof ICalParseError ? e.code : "parse" }
  }
}

/** A feed whose events are all unreadable: keep what was shown before. */
function isUnreadable(feed: ParsedFeed): boolean {
  return feed.events.length === 0 && feed.skipped.some((s) => s.reason !== "invalid-rrule")
}

export function statusFromFeed(feed: ParsedFeed, now: number): CalendarStatus {
  const skipped: SkippedEvent[] = feed.skipped.slice(0, MAX_SKIPPED_KEPT)
  return {
    lastOk: now,
    eventCount: feed.eventCount,
    recurringCount: feed.recurringCount,
    skipped,
    uncertainZones: feed.zones.filter((z) => z.uncertain).map((z) => z.tzid),
  }
}

/** The calendar after a fetch attempt. */
export function applyFetchOutcome(
  prevAny: CachedCalendar | undefined,
  target: { id: string; url: string },
  outcome: FetchOutcome,
  now: number,
): CachedCalendar {
  // Data fetched from another URL is not this calendar's data any more
  const prev = prevAny && prevAny.url === target.url ? prevAny : undefined
  const base: CachedCalendar = prev ?? {
    id: target.id,
    url: target.url,
    events: [],
    status: emptyStatus(),
  }

  if (outcome.kind === "error") {
    return {
      ...base,
      id: target.id,
      status: {
        ...base.status,
        lastError: { code: outcome.code, status: outcome.status, at: now },
      },
    }
  }

  if (outcome.kind === "unchanged") {
    const status: CalendarStatus = { ...base.status, lastOk: now }
    delete status.lastError
    return { ...base, id: target.id, contentHash: outcome.hash, status }
  }

  const { feed } = outcome
  if (isUnreadable(feed)) {
    return {
      ...base,
      id: target.id,
      status: {
        ...base.status,
        skipped: feed.skipped.slice(0, MAX_SKIPPED_KEPT),
        lastError: { code: "no-readable-events", at: now },
      },
    }
  }

  return {
    id: target.id,
    url: target.url,
    contentHash: outcome.hash,
    calendarName: feed.calendarName,
    events: feed.events,
    status: statusFromFeed(feed, now),
  }
}

/** Run `fn` over `items` with at most `limit` in flight. */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const i = next++
      results[i] = await fn(items[i]!)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return results
}
